// EsimDetailPage.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './EsimDetailsPage.css';
import EsimHeaderCard from "./component/EsimHeaderCard";
import { Download, ChevronRight, Package as PackageIcon, Calendar, ArrowUpDown, Filter, XCircle, AlertTriangle } from 'lucide-react';
import { PieChart, Pie, Cell } from 'recharts';
import TopupCarousel from "./component/TopupCarousel";
import RenameEsimModal from "./component/RenameEsimModal";
import ShareEsimModal from "./component/ShareEsimModal";
import ArchiveEsimModal from "./component/ArchiveEsimModal";
import { useLanguage } from "../i18n/LanguageContext";

// Labels live in esimLegacy.status.<STATUS>; the class name stays English
// whatever the UI language, since the stylesheet keys on it.
const STATUS_CLASS = {
    'ACTIVE' : 'active',
    'TERMINATED' : 'expired',
    'NOT_ACTIVE' : 'non-active'
}

const formatExpiry = (endTime) => {
    // turn "2025-07-28 06:01:00" → ISO string in UTC
    const dt = new Date(endTime.replace(' ', 'T') + 'Z')

    const mm   = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd   = String(dt.getUTCDate()).padStart(2, '0');
    const yyyy = dt.getUTCFullYear();
    const hour  = String(dt.getUTCHours()).padStart(2, '0')
    const minute= String(dt.getUTCMinutes()).padStart(2, '0')

    return `${dd}/${mm}/${yyyy} ${hour}:${minute} GMT`
}
const formatGB = (gbValue) => {
    if (gbValue === 0) return '0 GB';
    return `${gbValue.toFixed(1)} GB`;
};
const formatLocalDateTime = (value, locale) => {
    if (!value) return '';
    // Normalize timezone like +0000 -> +00:00 so Date can parse reliably
    const normalized = value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    const dt = new Date(normalized);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });
};
const EsimDetailPage = () => {
    const { iccid } = useParams();
    const [data, setData] = useState(null);
    const [showRename, setShowRename] = useState(false);
    const [showShareEmail, setShowShareEmail] = useState(false);
    const [showArchive, setShowArchive] = useState(false);
    const navigate = useNavigate();
    const { t, locale } = useLanguage();

    useEffect(() => {
        const fetchEsimData = async () => {
            try {
                const res = await fetch(`https://api.artamobile.id/v1/esims/${iccid}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setData(json);
            } catch (error) {
                console.error('Failed to fetch eSIM data:', error);
            }
        };

        fetchEsimData();
    }, [iccid]);


    if (!data) return <div>{t('esimLegacy.loading')}</div>;

    const { esim, region, plans, usage } = data;
    const total = Math.max(0, usage?.quota ?? 0);
    const remaining = Math.max(0, Math.min(total, usage?.balance ?? 0));
    const used = Math.max(0, total - remaining);
    // simType 0 is a physical SIM card; anything else (or a missing value) is an
    // eSIM. Keep this in step with EsimCard so a SIM reads the same in the list
    // and on its detail page. A physical SIM has nothing to install and can't be
    // "deleted", so the install guide and the deletion warning don't apply.
    const isPhysicalSim = parseInt(esim.simType, 10) === 0;
    const label = esim.label || `${isPhysicalSim ? "SIM" : "eSIM"} ${region.name}`;

    return (
        <div className="esim-detail-page">
            <EsimHeaderCard
                iccid={esim.iccid}
                region={{name: region.name, coverage: region.name}}
                imageUrl={region.shopImageUrl}
                label={label}
                recipient={esim.recipient}
                onEdit={() => setShowRename(true)}
                onShareEmail={() => setShowShareEmail(true)}
                onArchiveEsim={() => setShowArchive(true)}
            />

            <RenameEsimModal
                open={showRename}
                onClose={() => setShowRename(false)}
                currentLabel={label}
                simId={esim.SimId}
                onRenamed={(newLabel) => {
                    setData((prev) => ({
                        ...prev,
                        esim: {...prev.esim, label: newLabel},
                    }));
                }}
            />

            <ShareEsimModal
                open={showShareEmail}
                onClose={() => setShowShareEmail(false)}
                simId={esim.SimId}
                onShared={() => { /* optional analytics / toast */ }}
            />

            <ArchiveEsimModal
                open={showArchive}
                onClose={() => setShowArchive(false)}
                simId={esim.SimId}
                onArchived={() => {
                    // optional: update local state or navigate away
                    // e.g. mark status, or redirect to list page
                    // navigate('/esims');
                }}
            />


            {/* eSIM only; a physical SIM can't be deleted from the device */}
            {!isPhysicalSim && (
                <div className="warning-box">
                    <AlertTriangle size={20} className="warning-icon" />
                    <div className="warning-content">
                        <strong>{t('esimDetail.warningTitle')}</strong> {t('esimLegacy.warningBody')}
                    </div>
                </div>
            )}


            {/* eSIM only; a physical SIM is already in the device */}
            {!isPhysicalSim && (
                <section className="esim-installation">
                    <button
                        className="instruction-button"
                        onClick={() => {
                            navigate('/esims/install', { state: { qrCodeUrl: `https://artaqrcode.s3.ap-southeast-1.amazonaws.com/${esim.filename}`, activationCode: esim.activationCode }});
                        }}
                    >
                        <Download size={20} className="instruction-icon"/>
                        <span>{t('esimLegacy.install')}</span>
                        <ChevronRight size={20}/>
                    </button>
                </section>
            )}


            <section className="usage">
                <div className="usage-header">
                    <h3>{t('esimLegacy.usage')}</h3>
                    <div className="usage-header-right">
                        <Filter size={20}/>
                    </div>
                </div>
                <div className="usage-content">
                    <div className="gauge-block">
                        {/*<ArrowUpDown size={24} className="gauge-icon"/>*/}
                        {/*<div className="gauge-label">DATA</div>*/}
                        <PieChart width={160} height={140} className="gauge-chart">
                            <Pie
                                data={[{value: remaining }, {value: used}]}
                                startAngle={180}
                                endAngle={0}
                                innerRadius={45}
                                outerRadius={55}
                                dataKey="value"
                            >
                                <Cell fill="#6772E5"/>
                                <Cell fill="#DDE2FF"/>
                            </Pie>
                        </PieChart>
                        <div className="gauge-text">
                            <div className="gauge-value">{formatGB(usage.balance)}</div>
                            <div className="gauge-desc">{t('esimLegacy.remaining')}</div>
                        </div>
                    </div>

                    <div className="stats-block">
                        <p><span
                            className="stat-label">{t('esimDetail.usedData')}</span><br></br><strong>{formatGB(usage.quota - usage.balance)}</strong>
                        </p>
                        <p><span className="stat-label">{t('esimDetail.totalData')}</span><br></br>
                            <strong>{formatGB(usage.quota)}</strong></p>
                    </div>
                </div>
                {usage?.expiry && (
                    <div className="usage-expiry-row">
                        <div className="usage-expiry-pill">
                            <span className="usage-expiry-icon">
                                <Calendar size={20} />
                            </span>
                            <div className="usage-expiry-text">
                                <div className="usage-expiry-title">
                                    <span className="usage-expiry-label">{t('esimLegacy.expires')}</span>
                                </div>
                                <div className="usage-expiry-value">{formatLocalDateTime(usage.expiry, locale)}</div>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <section className="my-packages">
                <div className="my-packages-header">
                    <h3>{t('esimLegacy.myPackages')}</h3>
                    <PackageIcon size={24}/>
                </div>

                {plans?.map((pkg, idx) => (
                    <div className="package-card" key={idx}>
                        {pkg.status && (
                            <div className={`status-pill ${STATUS_CLASS[pkg.status]}`}>
                                {t(`esimLegacy.status.${pkg.status}`)}
                            </div>
                        )}

                        <div className="package-content">
                            {/* only render expiry if end_time is set */}
                            {pkg.expiry && pkg.expiry !== '0000-00-00 00:00:00' && (
                                <div className="package-row">
                                    <div className="label">
                                        <XCircle size={20}/>
                                        <span>{t('esimLegacy.expiry')}</span>
                                    </div>
                                    <div className="value">{formatExpiry(pkg.expiry)}</div>
                                </div>
                            )}

                            <div className="package-row">
                                <div className="label">
                                <Calendar size={20}/>
                                    <span>{t('esimLegacy.validity')}</span>
                                </div>
                                <div className="value">{t('esimLegacy.days', { n: pkg.validity || 0 })}</div>
                            </div>

                            <div className="package-row">
                                <div className="label">
                                    <ArrowUpDown size={20}/>
                                    <span>{t('esimLegacy.data')}</span>
                                </div>
                                <div className="value">{`${pkg.quota} GB`}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </section>
            {data && <TopupCarousel regionCode={region?.regionCode} iccid={iccid} SimId={esim?.SimId} />}


        </div>
    );
};

export default EsimDetailPage;
